import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { Button } from '../../components/atoms/Button';
import toast from 'react-hot-toast';

const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUserRole } = useAuth();
  const [selectedRole, setSelectedRole] = useState<'pet-owner' | 'clinic' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRoleSelect = (role: 'pet-owner' | 'clinic') => {
    setSelectedRole(role);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      toast.error(t('onboarding.pleaseSelectRole'));
      return;
    }

    setIsLoading(true);
    try {
      // Map selected role to UserRole enum
      const userRole = selectedRole === 'pet-owner' ? UserRole.CUSTOMER : UserRole.OWNER;

      // Update user role via API
      if (updateUserRole) {
        await updateUserRole(userRole);
      }

      toast.success(t('onboarding.roleSelected'));

      // Redirect based on selected role
      if (selectedRole === 'pet-owner') {
        navigate('/pet-owner-setup', { replace: true });
      } else {
        navigate('/clinic-setup', { replace: true });
      }
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(t('onboarding.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 py-12">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-indigo-100 p-4">
              <svg className="h-12 w-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t('onboarding.title')}
          </h1>
          <p className="mt-3 text-lg text-gray-600">
            {t('onboarding.subtitle')}
          </p>
        </div>

        {/* Role Selection Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Pet Owner Card */}
          <button
            onClick={() => handleRoleSelect('pet-owner')}
            className={`group relative overflow-hidden rounded-2xl border-2 p-8 text-left transition-all hover:shadow-xl ${
              selectedRole === 'pet-owner'
                ? 'border-indigo-600 bg-indigo-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-indigo-300'
            }`}
          >
            <div className="relative z-10">
              <div className="mb-4 inline-flex rounded-full bg-indigo-100 p-4">
                <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </div>
              {selectedRole === 'pet-owner' && (
                <div className="absolute right-6 top-6">
                  <div className="rounded-full bg-indigo-600 p-1">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                {t('onboarding.petOwner.title')}
              </h3>
              <p className="mb-4 text-gray-600">
                {t('onboarding.petOwner.description')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.petOwner.features.appointments')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.petOwner.features.records')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.petOwner.features.reminders')}
                </li>
              </ul>
            </div>
          </button>

          {/* Clinic Card */}
          <button
            onClick={() => handleRoleSelect('clinic')}
            className={`group relative overflow-hidden rounded-2xl border-2 p-8 text-left transition-all hover:shadow-xl ${
              selectedRole === 'clinic'
                ? 'border-purple-600 bg-purple-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-purple-300'
            }`}
          >
            <div className="relative z-10">
              <div className="mb-4 inline-flex rounded-full bg-purple-100 p-4">
                <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              {selectedRole === 'clinic' && (
                <div className="absolute right-6 top-6">
                  <div className="rounded-full bg-purple-600 p-1">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                {t('onboarding.clinic.title')}
              </h3>
              <p className="mb-4 text-gray-600">
                {t('onboarding.clinic.description')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.clinic.features.management')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.clinic.features.scheduling')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.clinic.features.inventory')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('onboarding.clinic.features.invoicing')}
                </li>
              </ul>
            </div>
          </button>
        </div>

        {/* Continue Button */}
        <div className="mt-12 flex justify-center">
          <Button
            variant="primary"
            size="large"
            onClick={handleContinue}
            isLoading={isLoading}
            disabled={!selectedRole}
            className="min-w-[200px] rounded-full px-8 py-4 text-base font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
          >
            {t('onboarding.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
